import { useNavigate } from 'react-router-dom';
import { PERIODS, TIERS, type Tier } from '@/lib/sponsor/catalog';
import { num, quote, usd } from '@/lib/sponsor/pricing';
import { useSponsorStore } from '@/lib/sponsor/store';
import { useSponsorContext } from './context';

const fmtStop = (k: number) => (k >= 1000 ? `${k / 1000}M` : `${k}K`);

export default function SponsorPlansPage() {
  const { selection, setSelection } = useSponsorContext();
  const store = useSponsorStore();
  const navigate = useNavigate();

  const choose = (tier: Tier) => {
    setSelection((s) => ({ ...s, tier: tier.id }));
    navigate(store.currentAccountId ? '/sponsor/checkout' : '/sponsor/register');
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col items-center gap-4 text-center">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-balance">
          Show your brand in every connected app.{' '}
          <span className="text-primary-600">Save up to {selection.segment === 'ngo' ? '40' : '30'}%</span> at launch.
        </h1>
        <p className="max-w-2xl text-foreground-600">
          Users see your ads in the IDI and Survey apps, answer your survey, earn points and spend them at partner shops. You get a live dashboard and a report.
        </p>

        <div className="flex gap-6" role="group" aria-label="Customer type">
          {([['biz', 'Business'], ['ngo', 'NGO & Ministry · 15% off']] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              aria-pressed={selection.segment === id}
              onClick={() => setSelection((s) => ({ ...s, segment: id }))}
              className={`pb-1 text-sm border-b-2 cursor-pointer ${selection.segment === id ? 'border-foreground-950 font-semibold' : 'border-transparent text-foreground-500'}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="max-w-full overflow-x-auto">
          <div className="inline-flex gap-1 p-1 rounded-xl bg-background-200/70 border border-background-200" role="group" aria-label="Period">
            {PERIODS.map((p) => (
              <button
                key={p.months}
                type="button"
                aria-pressed={selection.periodMonths === p.months}
                onClick={() => setSelection((s) => ({ ...s, periodMonths: p.months }))}
                className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap cursor-pointer ${selection.periodMonths === p.months ? 'bg-background-50 shadow-sm' : 'text-foreground-600'}`}
              >
                {p.label}
                {p.tag && <em className="not-italic text-primary-600 ml-1">{p.tag}</em>}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3 items-start">
        {TIERS.map((t) => {
          const i = selection.stops[t.id];
          const q = quote(t, i, selection.periodMonths, selection.segment);
          const n = t.stops.length;
          return (
            <article key={t.id} className={`bg-background-50 rounded-2xl overflow-hidden border ${t.featured ? 'border-2 border-primary-500' : 'border-background-200'}`}>
              {t.featured && <div className="bg-primary-500 text-background-50 text-xs font-bold text-center py-1.5">Launch pick · most sponsors choose Growth</div>}
              <div className="p-5 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                    {t.name}
                    {t.isNew && <span className="text-[10px] font-bold bg-primary-500 text-background-50 px-1.5 py-0.5 rounded">NEW</span>}
                  </h2>
                  {q.savePct > 0 && <span className="text-xs font-bold bg-primary-500 text-background-50 px-2 py-1 rounded-md">Save {q.savePct}%</span>}
                </div>
                <p className="text-sm text-foreground-500 min-h-10">{t.desc}</p>

                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className="font-heading font-extrabold text-4xl tracking-tight">{usd(q.paid)}</span>
                  <span className="text-sm">/ {q.months} month{q.months > 1 ? 's' : ''}</span>
                  {q.list > q.paid && <s className="text-sm text-foreground-400">{usd(q.list)}</s>}
                </div>
                <p className="text-xs text-foreground-500 -mt-2">
                  {usd(q.perMonth)} a month · ${q.cpm.toFixed(2)} per 1,000 views · +10% VAT
                </p>

                <div className="mt-2">
                  <label className="text-xs text-foreground-500" htmlFor={`stop-${t.id}`}>Views per month</label>
                  <input
                    id={`stop-${t.id}`}
                    type="range"
                    min={0}
                    max={n - 1}
                    step={1}
                    value={i}
                    onChange={(e) => setSelection((s) => ({ ...s, stops: { ...s.stops, [t.id]: Number(e.target.value) } }))}
                    className="w-full accent-primary-500"
                    aria-valuetext={`${num(t.stops[i] * 1000)} views a month`}
                  />
                  <div className="flex justify-between text-[11px] text-foreground-500">
                    {t.stops.map((s, k) => (
                      <span key={s} className={k === i ? 'font-bold text-foreground-950' : ''}>{fmtStop(s)}</span>
                    ))}
                  </div>
                </div>
                <p className="font-heading font-bold text-xl">{num(q.viewsPerMonth)} <small className="text-sm font-semibold">views / month</small></p>
                <p className="text-xs text-foreground-500 -mt-2">
                  About {num(q.viewsPerMonth / 30)} views a day in {t.apps.length > 2 ? 'all connected apps' : t.apps.join(' and ')}
                </p>

                <button
                  type="button"
                  onClick={() => choose(t)}
                  className={`w-full py-3 rounded-xl font-bold cursor-pointer ${t.featured ? 'bg-primary-500 text-background-50 hover:bg-primary-600' : 'bg-foreground-950 text-background-50 hover:bg-foreground-800'}`}
                >
                  Buy {t.name}
                </button>
                <p className="text-xs text-center text-foreground-500">Starts when OPS approves your ad · no auto-renew</p>

                {t.groups.map((g) => (
                  <div key={g.title} className="pt-2 flex flex-col gap-2">
                    <h3 className="text-sm font-bold">{g.title}</h3>
                    <ul className="flex flex-col gap-2 text-sm">
                      {g.items.map((it) => (
                        <li key={it.label} className={`grid grid-cols-[18px_1fr] gap-1.5 ${it.included ? '' : 'text-foreground-400'}`}>
                          <i className={it.included ? 'ri-check-line' : 'ri-close-line'} aria-hidden="true"></i>
                          <span>
                            <span className="sr-only">{it.included ? 'Included: ' : 'Not included: '}</span>
                            {it.label}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </article>
          );
        })}
      </div>

      <p className="text-sm text-center text-foreground-500">
        Every ad is marked "Sponsored" and checked by OPS. No alcohol, tobacco, gambling or politics. Extras like points packs are sold from the dashboard.
      </p>
    </div>
  );
}
